import { router } from '@inertiajs/react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useRouteParams } from '@/hooks/useRoutes'
import { CannedAnswerDialog } from './CannedAnswerDialog'

export interface CannedAnswerRow {
  id: string
  title: string
  body: string
  tags: string[]
  status: string
  use_count: number
  variables: string[]
  updated_at: string
}

interface CannedAnswersListProps {
  answers: CannedAnswerRow[]
}

export function CannedAnswersList({ answers }: CannedAnswersListProps) {
  const { withAccount } = useRouteParams()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<CannedAnswerRow | null>(null)

  const openNew = () => { setEditing(null); setDialogOpen(true) }
  const openEdit = (a: CannedAnswerRow) => { setEditing(a); setDialogOpen(true) }
  const handleDelete = (id: string) => {
    router.delete(`/${withAccount().account_id}/shortcuts/${id}`, { preserveScroll: true })
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>Canned answers</CardTitle>
          <CardDescription>
            Reusable replies you can drop into a conversation with one click. {answers.length} saved.
          </CardDescription>
        </div>
        <Button onClick={openNew} size="sm" className="cursor-pointer shrink-0">
          <Plus className="size-4 mr-1" /> New
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {answers.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-muted-foreground mb-3">No canned answers yet.</p>
            <Button onClick={openNew} variant="outline" size="sm" className="cursor-pointer">
              <Plus className="size-4 mr-1" /> Add your first answer
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {answers.map(a => (
              <li key={a.id} className="px-4 py-3 hover:bg-muted/30 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.title}</div>
                  <div className="text-xs text-muted-foreground truncate font-mono">{a.body}</div>
                  {a.tags.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {a.tags.map(t => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-xs font-mono text-muted-foreground shrink-0 w-12 text-right">
                  {a.use_count}<span className="text-muted-foreground/50"> uses</span>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(a)} className="cursor-pointer" title="Edit">
                    <Pencil className="size-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(a.id)} className="cursor-pointer" title="Delete">
                    <Trash2 className="size-3.5 text-rose-400" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <CannedAnswerDialog open={dialogOpen} onOpenChange={setDialogOpen} answer={editing} />
    </Card>
  )
}
